import bindings from "@/config/coverage-beta-hotkeys.json";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { focusEmptyAreaBox } from "@/utils/coverage-beta-focus";
import { isDev } from "@/utils/utils";
import { useEffect, useRef } from "react";
import type { ReactZoomPanPinchContentRef } from "react-zoom-pan-pinch";
import { useGate } from "statsig-react";

type CoverageBetaState = ReturnType<typeof useCoverageBetaStore.getState>;

/** One editable hotkey pairing (see config/coverage-beta-hotkeys.json). */
export interface HotkeyBinding {
  action: string;
  keys: string[];
  description: string;
}

/**
 * The editable hotkey pairings. Change which keys trigger an action in
 * config/coverage-beta-hotkeys.json — the behaviour of each action lives in
 * ACTION_HANDLERS below. Also exported so a shortcuts legend / help UI can list
 * the bindings from the same source of truth.
 */
export const COVERAGE_BETA_HOTKEYS = bindings as HotkeyBinding[];

/** Pretty labels for keys that shouldn't be shown as their raw event value. */
const KEY_LABELS: Record<string, string> = {
  " ": "Space",
  ArrowLeft: "←",
  ArrowRight: "→",
  ArrowUp: "↑",
  ArrowDown: "↓",
  Escape: "Esc",
  Delete: "Del",
  Backspace: "Bksp",
  Tab: "Tab",
  Shift: "Shift",
};

const formatKey = (key: string): string =>
  KEY_LABELS[key] ?? (key.length === 1 ? key.toUpperCase() : key);

/**
 * The display label for an action's primary key (e.g. "Q", "Space", "←"), for
 * use in button tooltips so the hint always matches the JSON binding. Returns ""
 * for an unknown action.
 */
export const hotkeyHint = (action: string): string => {
  const binding = COVERAGE_BETA_HOTKEYS.find((b) => b.action === action);
  return binding ? formatKey(binding.keys[0]) : "";
};

// Last known cursor position, so the zoom hotkeys can zoom *toward the mouse*
// (matching wheel zoom) rather than the image centre. Updated on mousemove.
const lastPointer = { x: 0, y: 0, seen: false };

// Multiplicative scale change per zoom-hotkey press.
const ZOOM_STEP = 1.4;

// Percentage-point change per brightness-hotkey press.
const BRIGHTNESS_STEP = 10;

/**
 * Zoom a react-zoom-pan-pinch view by `factor`, keeping the point under the
 * cursor fixed (like wheel zoom) instead of zooming about the centre. Falls back
 * to the view centre until a pointer move has been seen.
 */
const zoomAtPointer = (ref: ReactZoomPanPinchContentRef, factor: number) => {
  const { instance } = ref;
  const wrapper = instance.wrapperComponent;
  if (!wrapper) return;

  const { scale, positionX, positionY } = instance.transformState;
  const { minScale, maxScale } = instance.setup;
  const nextScale = Math.min(maxScale, Math.max(minScale, scale * factor));
  if (nextScale === scale) return;

  const rect = wrapper.getBoundingClientRect();
  const pointerX = lastPointer.seen
    ? lastPointer.x
    : rect.left + rect.width / 2;
  const pointerY = lastPointer.seen
    ? lastPointer.y
    : rect.top + rect.height / 2;
  // Cursor relative to the wrapper origin (the space positionX/Y live in).
  const cx = pointerX - rect.left;
  const cy = pointerY - rect.top;
  const ratio = nextScale / scale;
  // Translation that leaves the content point under the cursor unmoved.
  const nextX = cx - (cx - positionX) * ratio;
  const nextY = cy - (cy - positionY) * ratio;
  ref.setTransform(nextX, nextY, nextScale, 150);
};

/** The pan/zoom controller for the view the mouse is currently over. */
const zoomTargetRef = (s: CoverageBetaState) => {
  if (s.hoveredView === "reference") return s.referencePinchRef;
  // The base planogram viewer registers its controller; the reusable viewer
  // does not (it owns wheel/minimap zoom), so this is null there → no-op.
  if (s.hoveredView === "planogram") return s.planogramPinchRef;
  return s.pinchRef;
};

/**
 * Actions permitted in the read-only (admin) view — non-mutating view/navigation
 * only. Everything else is gated off when the store is read-only.
 *
 * QA mode is read-only as well: CoverageBetaTask hydrates a QA task through
 * `hydrateReadOnly` (which sets `isReadOnly`) before `setQaMode`, so the boxes
 * are frozen but the reviewer still needs every way of *looking* at them. Hence
 * zoom/reset and brightness are listed here too — they move the camera or set a
 * CSS filter and never touch the submission. Leaving them off is what made
 * F/G/H (and A/S/K) dead keys in QA.
 */
const READ_ONLY_ACTIONS = new Set([
  "prev_ref",
  "next_ref",
  "prev_area",
  "next_area",
  "toggle_view",
  "toggle_boxes",
  "zoom_in",
  "zoom_out",
  "reset_view",
  "brightness_up",
  "brightness_down",
  "brightness_reset",
]);

/**
 * Frame the active empty area's box in the query view — same affordance as
 * clicking its row in the summary. Runs after the active index has changed.
 *
 * Deliberately keyed on `activeEmptyAreaIdx`, not the QA pager: `prev_area` /
 * `next_area` operate on the normal draw/edit-flow index even in QA mode
 * (they're in READ_ONLY_ACTIONS), so this stays consistent with that — making
 * them drive the pager instead is a separate follow-up.
 */
const focusActiveEmptyAreaBox = () => {
  focusEmptyAreaBox(useCoverageBetaStore.getState().activeEmptyAreaIdx);
};

/**
 * What each action *does*, keyed by the `action` names used in the JSON. The
 * JSON owns which keys map to an action; this owns the behaviour + guards.
 */
const ACTION_HANDLERS: Record<
  string,
  (s: CoverageBetaState, e: KeyboardEvent) => void
> = {
  prev_ref: (s, e) => {
    e.preventDefault();
    s.prevRefImg();
    s.recordInteraction("ref_nav", "hotkey");
  },
  next_ref: (s, e) => {
    e.preventDefault();
    s.nextRefImg();
    s.recordInteraction("ref_nav", "hotkey");
  },
  toggle_view: (s, e) => {
    // Bound to Space — stop the page scrolling / a focused button activating.
    e.preventDefault();
    s.toggleRightPanelView();
    s.recordInteraction("toggle_view", "hotkey");
  },
  toggle_boxes: (s) => {
    s.toggleBoxes();
    s.recordInteraction("toggle_boxes", "hotkey");
  },
  prev_area: (s, e) => {
    // Bound to Tab/Shift: always suppress the default (esp. Tab's focus
    // traversal) — including on auto-repeat while held — but only step once per
    // physical press.
    e.preventDefault();
    if (e.repeat) return;
    s.prevEmptyArea();
    focusActiveEmptyAreaBox();
    s.recordInteraction("area_nav", "hotkey");
  },
  next_area: (s, e) => {
    e.preventDefault();
    if (e.repeat) return;
    s.nextEmptyArea();
    focusActiveEmptyAreaBox();
    s.recordInteraction("area_nav", "hotkey");
  },
  cursor: (s) => {
    s.setActiveMode("cursor");
    s.recordInteraction("cursor", "hotkey");
  },
  dismiss: (s) => {
    // Cancel the confirm step first if it's open; otherwise deselect the tool.
    if (s.confirming != null) {
      s.cancelConfirmingSelection();
    } else {
      s.setActiveMode("cursor");
      s.recordInteraction("cursor", "hotkey");
    }
  },
  draw_box: (s) => {
    // Blocked only while a confirm step is pending.
    if (s.confirming == null) {
      s.setActiveMode("draw-box");
      s.recordInteraction("draw_box", "hotkey");
    }
  },
  draw_person: (s) => {
    if (s.confirming == null) {
      s.setActiveMode("draw-person");
      s.recordInteraction("draw_person", "hotkey");
    }
  },
  zoom_in: (s) => {
    // Zoom whichever view the mouse is over, toward the cursor.
    const ref = zoomTargetRef(s);
    if (ref) zoomAtPointer(ref, ZOOM_STEP);
    s.recordInteraction("zoom_in", "hotkey");
  },
  zoom_out: (s) => {
    const ref = zoomTargetRef(s);
    if (ref) zoomAtPointer(ref, 1 / ZOOM_STEP);
    s.recordInteraction("zoom_out", "hotkey");
  },
  reset_view: (s) => {
    // Reset the view the mouse is over.
    if (s.hoveredView === "reference") s.referencePinchRef?.resetTransform();
    else if (s.hoveredView === "planogram") s.resetPlanogramView();
    else s.pinchRef?.resetTransform();
    s.recordInteraction("reset_view", "hotkey");
  },
  delete_area: (s, e) => {
    const active = s.idxToFeEmptyArea[s.activeEmptyAreaIdx];
    if (active && !active.isDeleted) {
      e.preventDefault();
      s.deleteFeEmptyArea(s.activeEmptyAreaIdx);
      s.recordInteraction("delete_area", "hotkey");
    }
  },
  no_match: (s) => {
    // While a confirm step is open, reject the product being confirmed.
    if (s.confirming != null) {
      s.rejectConfirmingSelection();
      s.recordInteraction("no_match", "hotkey");
      return;
    }
    // Otherwise mark the active empty area as "no match" (no product) — the
    // same action as the footer's "No match" button, so X works even when no
    // product has been clicked.
    const area = s.idxToFeEmptyArea[s.activeEmptyAreaIdx];
    if (area && !area.isDeleted) {
      s.toggleEmptyAreaNoMatch(s.activeEmptyAreaIdx);
      s.recordInteraction("no_match", "hotkey");
    }
  },
  brightness_up: (s) => {
    // Brightness applies to photo views only (query / reference), not planogram.
    if (s.hoveredView === "query" || s.hoveredView === "reference") {
      s.adjustBrightness(s.hoveredView, BRIGHTNESS_STEP);
      s.recordInteraction("brightness_up", "hotkey");
    }
  },
  brightness_down: (s) => {
    if (s.hoveredView === "query" || s.hoveredView === "reference") {
      s.adjustBrightness(s.hoveredView, -BRIGHTNESS_STEP);
      s.recordInteraction("brightness_down", "hotkey");
    }
  },
  brightness_reset: (s) => {
    if (s.hoveredView === "query" || s.hoveredView === "reference") {
      s.resetBrightness(s.hoveredView);
      s.recordInteraction("brightness_reset", "hotkey");
    }
  },
};

// View actions the reusable planogram viewer handles itself (its built-in
// a/s/k hotkeys, armed by hovering its viewport). When that viewer is enabled
// and the mouse is over the planogram, these must be left to it — running the
// legacy handlers too would double-handle (e.g. two resets on `k`).
const REUSABLE_PLANO_OWNED_ACTIONS = new Set([
  "zoom_in",
  "zoom_out",
  "reset_view",
]);

// Build a key → action lookup once from the JSON bindings.
const KEY_TO_ACTION: Record<string, string> = {};
for (const binding of COVERAGE_BETA_HOTKEYS) {
  for (const key of binding.keys) KEY_TO_ACTION[key] = binding.action;
}

// Dev-time guard: flag a binding whose action has no handler (a likely typo).
if (isDev()) {
  for (const b of COVERAGE_BETA_HOTKEYS) {
    if (!ACTION_HANDLERS[b.action]) {
      // eslint-disable-next-line no-console
      console.warn(
        `[coverage-beta hotkeys] binding "${b.action}" has no handler`,
      );
    }
  }
}

/** Ignore hotkeys while the user is typing in a field. Also used by the
 * onboarding tutorial's own key handler (CoverageBetaTrainingOverlay). */
export const isTypingTarget = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable
  );
};

/**
 * Global keyboard shortcuts for coverage-beta.
 *
 * Key→action pairings live in `config/coverage-beta-hotkeys.json` (easy to edit
 * / rebind); per-action behaviour + guards live in ACTION_HANDLERS above. Only
 * READ_ONLY_ACTIONS run in the read-only (admin) view.
 */
export const useCoverageBetaHotkeys = () => {
  // Same gate as CoverageBetaPlanogramView: when the reusable viewer renders
  // the planogram, its built-in hotkeys own zoom/reset over that view. Read
  // through a ref so the keydown listener (registered once) sees the live value.
  const { value: isReusablePlanoViewer } = useGate(
    "fe_enable_reusable_plano_viewer_coverage_beta",
  );
  const isReusablePlanoViewerRef = useRef(isReusablePlanoViewer);
  isReusablePlanoViewerRef.current = isReusablePlanoViewer;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const state = useCoverageBetaStore.getState();
      // The onboarding tutorial is fully modal — suppress every hotkey while
      // it's open so a labeler reading a step can't delete/no-match/toggle a
      // real box, or fight the tour for the right panel / active tool.
      if (state.tutorialActive) return;

      const action = KEY_TO_ACTION[e.key];
      if (!action) return;
      const handler = ACTION_HANDLERS[action];
      if (!handler) return;

      if (!READ_ONLY_ACTIONS.has(action) && state.isReadOnly) return;

      // Defer to the reusable planogram viewer's own hotkeys over its view.
      if (
        isReusablePlanoViewerRef.current &&
        state.hoveredView === "planogram" &&
        REUSABLE_PLANO_OWNED_ACTIONS.has(action)
      ) {
        return;
      }

      handler(state, e);
    };

    // Track the cursor so the zoom hotkeys can zoom toward it.
    const handlePointerMove = (e: MouseEvent) => {
      lastPointer.x = e.clientX;
      lastPointer.y = e.clientY;
      lastPointer.seen = true;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousemove", handlePointerMove);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousemove", handlePointerMove);
    };
  }, []);
};
